import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Keeps the Navidium Shipping Protection widget alive across cart-drawer re-renders.
 *
 * The drawer is re-rendered through the Section Rendering API on every cart mutation, which
 * destroys the `.nvd-mini` container the app embed injected before the checkout button. Rebuilding
 * it means calling the app's own `nvd_init()`, which re-runs `window.injectNvdToCart()` (defined by
 * the navidium-protection-consent embed, guarded by `window.needToInjectNvd`) and then re-hides the
 * theme's native checkout button itself. `injectNvdToCart` bails when the container already exists,
 * so calling `nvd_init()` more often than strictly needed is safe.
 *
 * The app's own recovery is not enough on this theme:
 *   - a 2s timeout hung off a click on one of its `click_triggers`;
 *   - an `NvdCartWatcher` (PerformanceObserver over resource timing) that only fires when the cart
 *     contents actually changed;
 *   - a `setInterval` retry that stops ~6s after DOMContentLoaded.
 * None of them covers a drawer re-render that changes no cart line, and the measured re-render is
 * slower than the 2s timeout.
 *
 * Two mechanisms below, because one signal cannot cover both re-render shapes:
 *
 * 1. `ThemeEvents.cartUpdate` — the fast path. Dispatched by assets/component-cart-items.js:190
 *    just BEFORE its `morphSection` call on line 198, so the listener must not run inline; the
 *    `requestAnimationFrame` defers it past that synchronous morph. Also covers add-to-cart, since
 *    `CartAddEvent.eventName` is `ThemeEvents.cartUpdate` (assets/events.js:116).
 *
 * 2. A self-healing observer — the safety net. When the theme has no `sections` payload to morph it
 *    falls back to `sectionRenderer.renderSection` (assets/component-cart-items.js:397 and :409,
 *    assets/cart-drawer.js:91, sections/cart-drawer-custom.liquid:239), an async fetch that lands
 *    ~600-750ms later and wipes whatever the fast path just rebuilt. Watching for the container to
 *    go missing covers that, the Rebuy paths, and `discount:update` — none of which the fast path
 *    can time correctly — with one mechanism instead of a list of events and guessed delays.
 *
 * Neither uses an AbortController, and that is deliberate: this module is bound once for the
 * lifetime of the document from layout/theme.liquid, outside any section, so there is no re-render
 * or disconnect that could orphan it.
 */

/** @type {boolean} Guards against re-initialising more than once per frame. */
let queued = false;

/**
 * @type {number} `nvd_init()` calls made since the container went missing. Reset once it is back,
 * and on every cart mutation.
 */
let unresolvedHeals = 0;

/**
 * Calls to `nvd_init()` allowed while the container stays missing.
 *
 * `nvd_init()` costs a `/cart.js` round trip, and this page already has ~8 independent
 * consumers of the cart endpoints (the app's own watcher, Elevar, Rebuy, Trekkie, …) which
 * together are enough to earn a 429 from Shopify on a preview domain. Without this cap, a
 * container that never comes back — the app embed failing to load, `needToInjectNvd`
 * unset, cart requests already rate-limited — would let the observer below fire once per
 * animation frame for as long as anything on the page keeps mutating, turning a broken
 * widget into a request storm that keeps itself broken.
 *
 * The budget counts *calls that were actually made*, not heals that were attempted — see
 * `reinitialiseNavidium`. It is spent per incident, and an incident ends either when the
 * container comes back or when the shopper touches the cart again.
 */
const MAX_UNRESOLVED_HEALS = 6;

/**
 * Ask the app embed to rebuild its widget, at most once per animation frame.
 * @returns {void}
 */
const reinitialiseNavidium = () => {
  if (queued) return;
  queued = true;

  requestAnimationFrame(() => {
    queued = false;

    try {
      // `nvd_init` is a top-level `const` in the app embed's classic script, so it lives in the
      // global lexical environment and not on `window` — it can only be read as a bare identifier.
      // If the extension failed to load, `typeof` yields 'undefined'; if the script has not
      // evaluated yet, reading it throws, hence the try.
      // @ts-ignore — declared by the Navidium app embed, not by the theme.
      if (typeof nvd_init !== 'function') return;

      // Counted here rather than at the call site on purpose. The budget exists to bound
      // `/cart.js` requests, so only a call that actually happens may spend it: an attempt made
      // before the app embed has evaluated costs nothing, and the `queued` flag above collapses
      // a burst of mutations into this single call. Counting at the call site charged for both,
      // and could retire the budget during page load without a single request being sent.
      unresolvedHeals++;

      // `nvd_init` is `async`, so the `try` around it only catches a synchronous throw. Its own
      // `/cart.js` and config fetches reject on a 429 or an offline tab, and without this that
      // surfaces as an "Uncaught (in promise)" the console cannot attribute to anything. There is
      // nothing to do about a failed rebuild but wait for the next signal, so it is swallowed here.
      // @ts-ignore — declared by the Navidium app embed, not by the theme.
      nvd_init()?.catch(() => {});
    } catch {
      // App embed not ready. Nothing to do but wait for the next signal.
    }
  });
};

document.addEventListener(ThemeEvents.cartUpdate, () => {
  // A cart mutation is a fresh incident and the shopper is waiting on this widget, so it starts
  // with a full budget. This is also the recovery path out of an exhausted one: without it, six
  // failed heals would silence the observer for the rest of the page's life.
  unresolvedHeals = 0;
  reinitialiseNavidium();
});

/**
 * Re-injects the widget whenever a re-render leaves a rendered cart without the app's container.
 *
 * Deliberately keyed on the symptom rather than on the event that caused it: the container is gone
 * while a cart is on screen. That settles on its own — once `nvd_init()` re-injects `.nvd-mini` the
 * condition is false, so the observer's own mutations do not feed back into it.
 *
 * The gate is the checkout button, not `cart-items-component`. Both mark "a cart is on screen", but
 * only the button marks a cart the app can attach to: `snippets/cart-summary.liquid` renders it
 * inside the non-empty branch of `sections/cart-drawer-custom.liquid`, and it is the very node the
 * app embed anchors to (`checkout_section: [name="checkout"]` in config/settings_data.json). So the
 * condition reads "the app has an anchor but no container", which is exactly when a heal is useful.
 *
 * `cart-items-component` renders on an empty cart too, and Navidium correctly never injects there —
 * so that gate was permanently true on an empty cart and spent the whole budget on every page load.
 * Measured on the home page, empty cart, before this change: 23 mutation batches met the condition,
 * the budget retired at 1772ms, and 6 of the page's 11 `/cart.js` requests were ours. After: zero.
 * Most sessions start with an empty cart, so this was the module's main cost in production.
 * @returns {void}
 */
const healMissingWidget = () => {
  if (!document.querySelector('[name="checkout"]')) return;

  if (document.querySelector('.nvd-mini')) {
    unresolvedHeals = 0;
    return;
  }

  if (unresolvedHeals >= MAX_UNRESOLVED_HEALS) return;

  reinitialiseNavidium();
};

new MutationObserver(healMissingWidget).observe(document.body, { childList: true, subtree: true });

/*
 * -------------------------------------------------------------------------------------------------
 * Second job: the protection line never outlives the trip to checkout (DEC-042).
 *
 * "Checkout +" adds Navidium's `shipping-protection` product ("Free Returns & Package Protection",
 * priced from the cart total at that moment) and redirects to checkout. The vendor takes the line
 * out again on every normal page load (`removeNavidium()` in nvd-widget-with-consent.js, v537,
 * except on /account/login, /account/register or with `window.keepNavidium`), and empties a cart
 * that holds nothing else (`nvdCheckCart`). Two paths escape both. Found 2026-11-03 in the iOS
 * simulator, on a cart left at $12.53 with no product in it:
 *
 *   1. Back from checkout restores the page from the back/forward cache. That is not a page load,
 *      and the vendor's `pageshow` handler only re-renders its widget, so the line stays. The
 *      subtotal and the free-shipping bar then count a fee the shopper has not chosen yet, and the
 *      shopper cannot remove their last product: Shopify answers 422 "Your cart is not valid" to
 *      any write that would leave the line alone (measured live on `/cart/change.js` and
 *      `/cart/update.js`, with the tier Navidium picks and with another one). The drawer animates
 *      the row out, gets the 422 and redraws it, with nothing said.
 *   2. Removing the last product leaves the line alone in the cart. The vendor's empty-cart check
 *      did not fire, and the drawer showed a $12.53 subtotal over an empty list. Checkout charged
 *      exactly that: one "Free Returns & Package Protection" line.
 *
 * Both are closed here by doing what the vendor already does on a load. The vendor's own
 * `cart_protection` key goes too: it reads it to decide whether "Checkout +" still has to add the
 * line, so a stale one would send the next protected checkout out without protection.
 */

/** @type {string} Marks the cart update this module dispatches, so it never reacts to its own. */
const CLEANUP_SOURCE = 'navidium-protection-cleanup';

/** @type {string[]} Where the vendor keeps the line: checkout can send the shopper through them. */
const KEEP_PROTECTION_ON = ['/account/login', '/account/register'];

/**
 * The vendor's own test (`nvdIsProtectionHandle`), so the two can never disagree about a line.
 * @param {{ handle?: unknown }} item A cart line.
 * @returns {boolean}
 */
const isProtectionLine = (item) => {
  const handle = item?.handle;
  if (typeof handle !== 'string' || handle === '') return false;
  if (handle.includes('shipping-protection')) return true;

  try {
    // @ts-ignore — a top-level `const` of the Navidium app embed, readable only as a bare identifier.
    return typeof nvdWidgetHelpers !== 'undefined' && Boolean(nvdWidgetHelpers.product_handle?.includes(handle));
  } catch {
    return false;
  }
};

/** @type {boolean} One removal at a time. */
let droppingProtection = false;

/**
 * Takes the given protection lines out of the cart, then redraws the drawer and the bubble from the
 * answer: `sections` lets assets/component-cart-items.js morph without a second request, and
 * `background` keeps assets/cart-drawer.js from opening the drawer.
 * @param {Array<{ key: string }>} lines
 * @returns {Promise<void>}
 */
const dropProtection = async (lines) => {
  if (droppingProtection || lines.length === 0) return;
  droppingProtection = true;

  try {
    const sections = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component')]
          .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
          .filter(Boolean)
      ),
    ];

    // The `.js` endpoint on purpose, like the vendor's own removal. Measured 2026-11-03 from an
    // automated session that Cloudflare scores as low trust: `/cart/update` and `/cart/change`
    // answered 403 while `/cart/update.js` and `/cart/change.js` answered 200, `sections` included.
    const response = await fetch(
      `${Theme.routes.cart_update_url}.js`,
      fetchConfig('json', {
        body: JSON.stringify({
          updates: Object.fromEntries(lines.map((line) => [line.key, 0])),
          ...(sections.length > 0 && { sections, sections_url: window.location.pathname }),
        }),
      })
    );
    if (!response.ok) throw new Error(`cart update answered ${response.status}`);

    const cart = await response.json();

    try {
      localStorage.removeItem('cart_protection');
    } catch {
      // Storage blocked: the vendor re-derives the key from the cart on its next check.
    }

    document.dispatchEvent(
      new CartUpdateEvent(cart, 'navidium-widget', {
        itemCount: cart.item_count,
        source: CLEANUP_SOURCE,
        background: true,
        sections: cart.sections,
      })
    );
  } catch (error) {
    console.error('Removing the leftover Navidium protection line failed:', error);
  } finally {
    droppingProtection = false;
  }
};

// Path 2: a cart change that leaves the protection line with nothing to protect. Only a full cart
// can tell (a `/cart/change` answer, a resync); an add announces an empty resource and cannot
// leave the line alone anyway.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const { resource, data } = /** @type {any} */ (event).detail ?? {};
  if (data?.source === CLEANUP_SOURCE) return;

  const items = resource?.items;
  if (!Array.isArray(items) || items.length === 0) return;
  if (items.every(isProtectionLine)) dropProtection(items);
});

// Path 1: back from checkout. The vendor clears the line on a load; a page restored from the
// back/forward cache is not one.
window.addEventListener('pageshow', async (event) => {
  if (!event.persisted) return;
  if (KEEP_PROTECTION_ON.includes(window.location.pathname)) return;
  // @ts-ignore — the vendor's documented opt-out, set by the merchant when the line must stay.
  if (window.keepNavidium) return;

  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' });
    if (!response.ok) return;

    const cart = await response.json();
    const lines = (cart.items ?? []).filter(isProtectionLine);
    if (lines.length > 0) await dropProtection(lines);
  } catch (error) {
    console.error('Checking the cart for a leftover Navidium protection line failed:', error);
  }
});
